import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useDockHeight } from './dock.js'
import { useNudgeShake } from './Nudge.js'

/**
 * The control the table is waiting on, taking the screen until it is answered.
 *
 * Everything the game asks of a player is one tap on one card, and that card
 * used to sit in the page flow above a board several screens tall. On a phone
 * that is a control you can scroll away from: people read the track, argued
 * about it, and then could not find the thing they were being asked for -
 * which on a game where nine other people are waiting is the single worst
 * place to lose a button. Open, this is the whole screen, so there is nowhere
 * for it to be lost to.
 *
 * The handle is what makes that bearable, and it is the entire difference
 * between this and the full-bleed "YOUR TURN" interstitial the app had first
 * and threw away. That screen covered the board you need in order to decide
 * and gave you no way back to it; it was spent the moment it appeared. This
 * one says what is outstanding, and one tap folds it down to that single line
 * with the board behind it - and one tap brings it back, as many times as it
 * takes to make your mind up. Nothing is dismissed by answering it except the
 * question.
 *
 * The secret drawer is deliberately left uncovered underneath. Re-checking
 * which of three fascists you are is a thing people do *while* deciding, and
 * folding the sheet away to do it would take the decision off the screen.
 *
 * Above a certain width none of this is a problem - a laptop lays the board
 * out beside the controls and nothing is below the fold - so the stylesheet
 * puts the sheet back in the column, and the measurement below asks the
 * element whether it is still docked rather than re-deriving the breakpoint.
 */
export function ActionSheet({
  label,
  signature,
  offline = false,
  children,
}: {
  /** What the game is asking for, in four words. The handle carries it. */
  label: string
  /** Changes when the pending action does, which re-opens a collapsed sheet. */
  signature: string
  /**
   * The socket is down. Carried here because open, this covers the header the
   * connection state is otherwise reported in - and a phone whose buttons have
   * quietly stopped working is the one thing the player must not have to guess
   * at. The rest of that header is a tap away behind the handle; this is not.
   */
  offline?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(true)
  const ref = useRef<HTMLDivElement | null>(null)
  /*
   * Folded, this bar is the only thing left of the pending card, so it takes
   * over the card's twitch as well as its label - same clock, same half
   * second. Open, the card inside is doing it and the sheet holds still.
   */
  const shaking = useNudgeShake()

  /*
   * Collapsing is for reading the board during one decision, not a preference
   * to be carried into the next one. A sheet left shut from the last round
   * would hide the next thing the table is waiting on behind a line of text,
   * which is the failure this whole component exists to prevent.
   */
  useEffect(() => {
    setOpen(true)
  }, [signature])

  /*
   * How much of the viewport is spoken for, measured from the top of the sheet
   * to the bottom of the window - so the drawer underneath it and the safe
   * area under that are both counted without this having to know about
   * either. The page pads itself by that much; so does the hold hint.
   */
  useDockHeight(ref, '--sheet-h')

  return (
    <div
      ref={ref}
      className={`sheet ${open ? 'is-open' : 'is-shut'} ${!open && shaking ? 'is-nudged' : ''}`}
      role="region"
      aria-label={label ? `Your turn: ${label}` : 'Your turn'}
      data-testid="action-sheet"
    >
      {/*
        The handle is the whole of the collapsed state, so it says what is
        outstanding rather than merely offering a chevron. That is also why the
        nudge's floating line stands down while a sheet is up - see the
        `anchored` prop on NudgeProvider - the two of them would otherwise
        print the same sentence twice, a few pixels apart.
      */}
      <button
        type="button"
        className="sheet__handle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="action-sheet-toggle"
      >
        <span className="sheet__grip" aria-hidden="true" />
        <span className="sheet__kicker">Your turn</span>
        <span className="sheet__label">{label}</span>
        {offline && <span className="sheet__offline">reconnecting…</span>}
        <span className="sheet__more">{open ? 'see the board' : 'open'}</span>
        <span className={`sheet__chevron ${open ? '' : 'is-up'}`} aria-hidden="true" />
      </button>
      {/*
        Hidden with display rather than unmounted. The card registers itself
        with the nudge and holds the state of any press-and-hold in progress,
        and a player who folded the sheet away to look at the board should come
        back to the screen they left - and should still be buzzed at while they
        are over there.
      */}
      <div className="sheet__body">{children}</div>
    </div>
  )
}
